import { BleService, ScanResult } from "@capacitor-community/bluetooth-le";

export interface ReaderChannel {
  service: string;
  notify: string;
}

export interface ReaderProfile {
  id: string;
  model: string;
  names: RegExp;
  channels: readonly ReaderChannel[];
  protocol: "text" | "halo" | "hr0";
  setup: string;
  qualification: string;
}

const uart = {
  service: "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
  notify: "6e400003-b5a3-f393-e0a9-e50e24dcca9e",
};
const serialChannels: readonly ReaderChannel[] = [
  uart,
  { service: "49535343-fe7d-4ae5-8fa9-9fafd205e455", notify: "49535343-1e4d-4bd9-ba61-23c647249616" },
  { service: "0000ffe0-0000-1000-8000-00805f9b34fb", notify: "0000ffe4-0000-1000-8000-00805f9b34fb" },
  { service: "0000ffe0-0000-1000-8000-00805f9b34fb", notify: "0000ffe1-0000-1000-8000-00805f9b34fb" },
  { service: "0000fff0-0000-1000-8000-00805f9b34fb", notify: "0000fff1-0000-1000-8000-00805f9b34fb" },
];
const untested = "Untested: field testing needed";

export const READER_PROFILES: readonly ReaderProfile[] = [
  {
    id: "sdl440s",
    model: "Shearwell SDL440S",
    names: /^SDL440S?(?:[ _-]|$)/i,
    channels: [{ service: "2456e1b9-26e2-8f83-e744-f34f01e9d701", notify: "2456e1b9-26e2-8f83-e744-f34f01e9d703" }],
    protocol: "text",
    setup:
      "Turn on the reader. Match the SDL440 name to its label and confirm it is an SDL440S. Close other connected apps. Use decimal EID output with a line ending.",
    qualification: untested,
  },
  {
    id: "halo",
    model: "FlockFinder FDX Reader",
    names: /^Halo\+(?:[ _-]|$)/i,
    channels: [{ service: "00001811-0000-1000-8000-00805f9b34fb", notify: "00002a46-0000-1000-8000-00805f9b34fb" }],
    protocol: "halo",
    setup: "Turn on your FlockFinder FDX Reader and keep it near the phone. Close any other app connected to it.",
    qualification: "Android setup tested on firmware 8.6. Full qualification pending.",
  },
  ...["SRS2i", "XRS2i", "XRP2i"].map(model => ({
    id: model.toLowerCase(),
    model: `Tru-Test ${model}`,
    names: new RegExp(`^${model}(?:[ _-]|$)`, "i"),
    channels: [uart],
    protocol: "text" as const,
    setup:
      "Enable Bluetooth on the reader and select its BLE connection. Use decimal EID output with a line ending. Close other connected apps.",
    qualification: untested,
  })),
  ...["AWR250", "AWR300"].map(model => ({
    id: model.toLowerCase(),
    model: `Agrident / Allflex ${model}`,
    names: new RegExp(`^${model}(?:[ _-]|$)`, "i"),
    // Resolve the serial transport from the services actually exposed by this firmware.
    channels: serialChannels,
    protocol: "text" as const,
    setup:
      "In Setup > Interface Setup > Bluetooth, select Slave Mode and the BLE profile. Enable online output and choose decimal EIDs with a line ending. Close other connected apps.",
    qualification: untested,
  })),
  {
    id: "hr0",
    model: "Gallagher HR0",
    names: /^(?:HR0|H0)(?:[ _-]|$)/i,
    channels: [{ service: "f9c5aa00-e843-4ab3-b7ec-bf840ac14980", notify: "f9c5aa01-e843-4ab3-b7ec-bf840ac14980" }],
    protocol: "hr0",
    setup:
      "Turn on the reader and close other connected apps. Bluetooth is enabled while the HR0 is on. This integration is untested. Compare known tags before live scanning. Incomplete packets are rejected; contact FlockFinder support if scans are missing or differ.",
    qualification: untested,
  },
];

export function identifyReader(device: ScanResult): ReaderProfile | undefined {
  return READER_PROFILES.find(profile => profile.names.test(device.localName || device.device.name || ""));
}

export function readerDisplayName(device: ScanResult): string {
  const profile = identifyReader(device);
  return profile?.id === "halo" ? profile.model : device.localName || device.device.name || "EID reader";
}

function normaliseUuid(uuid: string): string {
  const lower = uuid.toLowerCase();
  return /^[0-9a-f]{4}$/.test(lower) ? `0000${lower}-0000-1000-8000-00805f9b34fb` : lower;
}

export function selectReaderChannel(profile: ReaderProfile, services: BleService[]): ReaderChannel | undefined {
  const matches = profile.channels.filter(channel =>
    services.some(
      service =>
        normaliseUuid(service.uuid) === channel.service &&
        service.characteristics.some(
          characteristic =>
            normaliseUuid(characteristic.uuid) === channel.notify &&
            (characteristic.properties.notify || characteristic.properties.indicate),
        ),
    ),
  );
  // Choosing between two serial outputs could duplicate or misinterpret scans.
  return matches.length === 1 ? matches[0] : undefined;
}
